<script setup lang="ts">
/* ============================================================
   TabBar — new-tab · tabs (dot/short-hash/name/close) · font · theme

   Ports the prototype's tab bar: hash dot colored from the hash,
   4-char short hash, ellipsized filename, hover-revealed close-X
   (close confirms via useTabs when the tab carries work), plus the
   font A−/A+ controls and the theme picker menu.
   ============================================================ */

import { ref, onMounted, onBeforeUnmount } from 'vue'
import type { Tab } from '@/types'
import { useTheme } from '@/composables/useTheme'
import { useReviewPrefs } from '@/composables/useReviewPrefs'
import { hashColor } from '@/composables/utils'

defineProps<{
  tabs: readonly Tab[]
  activeHash: string | null
  /** Whether the pinned Welcome tab (Welcome.md / Welcome back) is active. */
  welcomeActive?: boolean
  /** Label for the pinned Welcome tab: "Welcome.md" or "Welcome back". */
  welcomeName?: string
  /** Whether the pinned, right-justified Docs.md tab is active. */
  docsActive?: boolean
  /** Portable-snapshot mode: hide server-dependent controls (open file, docs). */
  embedded?: boolean
}>()

const emit = defineEmits<{
  (e: 'open-file'): void
  (e: 'paste-markdown'): void
  (e: 'paste-clipboard'): void
  (e: 'welcome'): void
  (e: 'docs'): void
  (e: 'install'): void
  (e: 'activate', hash: string): void
  (e: 'close', hash: string): void
}>()

const { themes, themeId, setTheme, bumpFont, resetFont, fontSize } = useTheme()
const { showAgent, showResolved, setShowAgent, setShowResolved } = useReviewPrefs()

// The settings dropdown — font size + theme, collapsed behind one gear.
const settingsOpen = ref(false)
const settingsRoot = ref<HTMLElement | null>(null)

// The + (new document) dropdown — upload / paste / from clipboard.
const plusOpen = ref(false)
const plusRoot = ref<HTMLElement | null>(null)

// The tab switcher (mobile only) — collapses the user-tab strip to one tab and
// hangs every open doc in a dropdown so you can pan between them on a phone.
const switcherOpen = ref(false)
const switcherRoot = ref<HTMLElement | null>(null)

function toggleSwitcher(): void {
  switcherOpen.value = !switcherOpen.value
}
function switcherPick(hash: string): void {
  switcherOpen.value = false
  emit('activate', hash)
}

function togglePlus(): void {
  plusOpen.value = !plusOpen.value
}
function plusPick(action: 'open-file' | 'paste-markdown' | 'paste-clipboard'): void {
  plusOpen.value = false
  // Narrow to each literal so emit()'s per-event overloads resolve.
  if (action === 'open-file') emit('open-file')
  else if (action === 'paste-markdown') emit('paste-markdown')
  else emit('paste-clipboard')
}

function toggleSettings(): void {
  settingsOpen.value = !settingsOpen.value
}
function onDocClick(e: MouseEvent): void {
  const target = e.target as Node
  if (settingsOpen.value && settingsRoot.value && !settingsRoot.value.contains(target))
    settingsOpen.value = false
  if (plusOpen.value && plusRoot.value && !plusRoot.value.contains(target)) plusOpen.value = false
  if (switcherOpen.value && switcherRoot.value && !switcherRoot.value.contains(target))
    switcherOpen.value = false
}
function onDocKeydown(e: KeyboardEvent): void {
  if (e.key !== 'Escape') return
  if (settingsOpen.value) settingsOpen.value = false
  if (plusOpen.value) plusOpen.value = false
  if (switcherOpen.value) switcherOpen.value = false
}
// Picking a theme leaves the settings menu open so font/theme can be tuned together.

onMounted(() => {
  document.addEventListener('click', onDocClick)
  document.addEventListener('keydown', onDocKeydown)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick)
  document.removeEventListener('keydown', onDocKeydown)
})
</script>

<template>
  <div class="tab-bar-shell">
    <div
      v-if="!embedded"
      class="tab tab-pinned tab-welcome"
      role="tab"
      :aria-selected="welcomeActive"
      :title="welcomeName"
      @click="emit('welcome')"
    >
      <svg
        class="tab-home-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
      <span class="tab-name">{{ welcomeName }}</span>
    </div>

    <div class="tab-bar" role="tablist">
      <div
        v-for="tab in tabs"
        :key="tab.hash"
        class="tab"
        role="tab"
        :aria-selected="!welcomeActive && !docsActive && tab.hash === activeHash"
        @click="emit('activate', tab.hash)"
        @mousedown.middle.prevent="emit('close', tab.hash)"
      >
        <span class="hash-dot" :style="{ '--dot': hashColor(tab.hash) }"></span>
        <span class="hash-short">{{ tab.shortHash }}</span>
        <span class="tab-name" :title="tab.filename">{{ tab.filename }}</span>
        <span
          class="tab-close"
          role="button"
          aria-label="Close tab"
          @click.stop="emit('close', tab.hash)"
          >×</span
        >
      </div>
    </div>

    <!-- Mobile tab switcher: the strip above collapses to just the active tab
         (CSS), and every open doc lives here so you can pan between them on a
         phone. Hidden on desktop, where the full strip is shown. -->
    <div
      v-if="!embedded && tabs.length"
      ref="switcherRoot"
      class="tab-switcher"
    >
      <button
        class="tab-switcher-btn"
        title="Open documents"
        aria-label="Open documents"
        aria-haspopup="true"
        :aria-expanded="switcherOpen"
        @click.stop="toggleSwitcher"
      >
        <span class="tab-switcher-count">{{ tabs.length }}</span>
        <svg
          class="tab-switcher-caret"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      <div v-show="switcherOpen" class="tab-switcher-popup" role="menu">
        <button
          v-for="tab in tabs"
          :key="tab.hash"
          class="tab-switcher-row"
          :class="{ 'is-active': !welcomeActive && !docsActive && tab.hash === activeHash }"
          role="menuitem"
          @click="switcherPick(tab.hash)"
        >
          <span class="hash-dot" :style="{ '--dot': hashColor(tab.hash) }"></span>
          <span class="hash-short">{{ tab.shortHash }}</span>
          <span class="tab-switcher-name" :title="tab.filename">{{ tab.filename }}</span>
          <span
            class="tab-switcher-close"
            role="button"
            aria-label="Close tab"
            @click.stop="emit('close', tab.hash)"
            >×</span
          >
        </button>
      </div>
    </div>

    <div v-if="!embedded" ref="plusRoot" class="plus-menu">
      <button
        class="btn btn-ghost new-tab-btn"
        title="New document"
        aria-label="New document"
        aria-haspopup="true"
        :aria-expanded="plusOpen"
        @click.stop="togglePlus"
      >
        +
      </button>
      <div v-show="plusOpen" class="plus-menu-popup" role="menu">
        <button class="plus-option" role="menuitem" @click="plusPick('open-file')">
          <span class="plus-option-icon" aria-hidden="true">⤒</span>
          <span class="plus-option-label">Upload file…</span>
        </button>
        <button class="plus-option" role="menuitem" @click="plusPick('paste-markdown')">
          <span class="plus-option-icon" aria-hidden="true">¶</span>
          <span class="plus-option-label">Paste markdown…</span>
        </button>
        <button class="plus-option" role="menuitem" @click="plusPick('paste-clipboard')">
          <span class="plus-option-icon" aria-hidden="true">📋</span>
          <span class="plus-option-label">Paste from clipboard</span>
        </button>
      </div>
    </div>

    <!-- Connect-your-agent bootstrap: opens the install-prompt modal rather
         than a document, so it's a button, not a tab. -->
    <div
      v-if="!embedded"
      class="tab tab-pinned tab-install"
      role="button"
      title="Connect your agent — install the redline skill"
      @click="emit('install')"
    >
      <svg
        class="tab-doc-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
      </svg>
      <span class="tab-name">Install</span>
    </div>

    <div
      v-if="!embedded"
      class="tab tab-pinned tab-docs"
      role="tab"
      :aria-selected="docsActive"
      title="Docs"
      @click="emit('docs')"
    >
      <svg
        class="tab-doc-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <line x1="10" y1="9" x2="8" y2="9" />
      </svg>
      <span class="tab-name">Docs</span>
    </div>

    <div class="tab-bar-ctrls">
      <div ref="settingsRoot" class="settings-menu">
        <button
          class="tab-ctrl tab-ctrl-settings"
          title="Settings"
          aria-label="Settings"
          aria-haspopup="true"
          :aria-expanded="settingsOpen"
          @click.stop="toggleSettings"
        >
          <svg
            class="settings-cog"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="3" />
            <path
              d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
            />
          </svg>
          <span>Settings</span>
        </button>
        <div v-show="settingsOpen" class="settings-menu-popup" role="menu">
          <div class="settings-label">Text size</div>
          <div class="settings-font">
            <button
              class="settings-font-btn"
              title="Decrease font size (Cmd/Ctrl + -)"
              aria-label="Decrease font size"
              @click="bumpFont(-1)"
            >
              A−
            </button>
            <span class="settings-font-value">{{ fontSize }}px</span>
            <button
              class="settings-font-btn"
              title="Increase font size (Cmd/Ctrl + +)"
              aria-label="Increase font size"
              @click="bumpFont(+1)"
            >
              A+
            </button>
            <button class="settings-font-reset" @click="resetFont">Reset</button>
          </div>

          <div class="settings-label">Theme</div>
          <button
            v-for="t in themes"
            :key="t.id"
            class="theme-option"
            :class="{ 'is-active': t.id === themeId }"
            role="menuitemradio"
            :aria-checked="t.id === themeId"
            @click="setTheme(t.id)"
          >
            <span
              class="theme-swatch"
              :style="{ '--swatch-bg': t.bg, '--swatch-fg': t.fg }"
            ></span>
            <span class="theme-option-name">{{ t.name }}</span>
            <span class="theme-option-check" aria-hidden="true">✓</span>
          </button>

          <div class="settings-label">Review</div>
          <button
            class="settings-toggle"
            role="menuitemcheckbox"
            :aria-checked="showAgent"
            @click="setShowAgent(!showAgent)"
          >
            <span class="settings-toggle-box" :class="{ 'is-on': showAgent }" aria-hidden="true">✓</span>
            <span class="settings-toggle-label">Show agent notes</span>
          </button>
          <button
            class="settings-toggle"
            role="menuitemcheckbox"
            :aria-checked="showResolved"
            @click="setShowResolved(!showResolved)"
          >
            <span class="settings-toggle-box" :class="{ 'is-on': showResolved }" aria-hidden="true">✓</span>
            <span class="settings-toggle-label">Show resolved comments</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
